import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { isValid} from 'date-fns';
import * as _ from 'lodash-es';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { HolidayService } from '../services/holiday.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { IHoliday } from '../shared/models/holiday';
import { ToastService } from '../shared/toast/toast.service';
import { DeleteActionComponent } from './delete-action.component';

@Component({
  selector: 'pps-manage-holiday',
  templateUrl: './manage-holiday.component.html',
  styles: [
  ],
})
export class ManageHolidayComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Holiday", icon: "pi-fw fas fa-umbrella-beach"},
  ];

  oldHolidays : IHoliday[] = [];
  holidays: IHoliday[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => param.data.id,
    defaultColDef:{
      enableCellChangeFlash: true,
      resizable: true
    }
  };

  private gridApi;

  constructor(private _holidayService: HolidayService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private _confirm: ConfirmationService,
              private _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver,) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  addHoliday(){
    let holiday: IHoliday = {
      id: 0,
      day: new Date(),
      holidayName: "Holiday name"
    };

    this._spinner.show(this.spinnerName);

    this.subs.sink = this._holidayService.add(holiday)
      .subscribe(res => {
        if (res.succeed){
          this.holidays.unshift(res.result);
          this.oldHolidays = _.cloneDeep(this.holidays);
          this.gridApi.setRowData(this.holidays);
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
            window.setTimeout( () =>{
              this.gridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "day"});
            }, 500);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.holidays.length = 0;
    this.subs.sink = this._holidayService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.holidays = apiResponse.result;
          this.oldHolidays = _.cloneDeep(this.holidays);
          this._toast.succeeded(`${this.holidays.length} records loaded`);
        }else{
          this._toast.failed(`Failed to load holiday data: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  delete(holiday: IHoliday){
    if (holiday === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Holiday`,
      message: `Are you sure that you want to delete [${holiday.holidayName}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(holiday)
    });
  }

  deleteHelper(holiday: IHoliday){
    this.subs.sink = this._holidayService.delete(holiday.id)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.holidays.findIndex(x => x.id == holiday.id);
          if (foundIndex !== -1 ){
            this.holidays.splice(foundIndex, 1);
            this.oldHolidays = _.cloneDeep(this.holidays);
            this.gridApi.setRowData(this.holidays);
            this._toast.succeeded(`[${holiday.holidayName}] deleted!`);
          }else{
            this._toast.failed(`Delete [${holiday.holidayName}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${holiday.holidayName}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }

  private getActionComponentTooltip(holiday: IHoliday):string{
    return "Click to delete holiday: " + holiday.holidayName;
  }  

  update(holiday: IHoliday){
    this._spinner.show(this.spinnerName);
    this.subs.sink = this._holidayService.update(holiday)
      .subscribe(res => {
        if (res.succeed){
          this._toast.succeeded(`Holiday updated`);
          holiday.day = res.result.day;
          holiday.holidayName = res.result.holidayName;
          this.oldHolidays = _.cloneDeep(this.holidays);
        }else{
          this._toast.failed(`Holiday update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        resizable: false,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Day',
        field:'day',
        filter: true,
        floatingFilter: true,
        width: 150,
        editable: true,
        sortable: true,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        valueGetter: this.getDateValue,
        onCellValueChanged: this.dataChanged
      },
      {
        colId: "holidayName",
        headerName: 'HolidayName',
        field:'holidayName',
        filter: true,
        floatingFilter: true,
        width: 500,
        editable: true,
        sortable: true,
        onCellValueChanged: this.dataChanged,
        headerClass: "ag-ext-header-align-left"
      }
    ];

    return cols;
  }

  private getDateValue(params){
    let currentHoliday = params.data;
    let util = params.context.componentParent._util;
    let dayValue = currentHoliday[params.colDef.field];

    if (typeof dayValue === "string"){
      dayValue = util.getDate(dayValue);
      if (!isValid(dayValue)){
        dayValue = params.context.componentParent.getHoliday(currentHoliday.id)?.day;
      }
    }

    let dateValue = util.getDateString(dayValue);
    return dateValue;
  }

  private dataChanged(params){
    let _this = params.context.componentParent;
    let data = params.data;
    let util = _this._util;
    let dayValue = data[params.colDef.field];

    if (params.colDef.field === "day" && typeof dayValue === "string"){
      dayValue = util.getDate(dayValue);
      if (!isValid(dayValue)){
        _this._toast.failed(`The value (${data[params.colDef.field]}) is not a valid date format. Valid date format: dd/mm/yyyy`, 10000);

        let rowNode = _this.gridApi.getRowNode(data.id);
        if (rowNode){
          _this.gridApi.flashCells({rowNodes: [rowNode]});
          window.setTimeout( () =>{
            rowNode.setSelected(true);
            _this.gridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "day"});
          }, 500);
        }
        return;
      }else{
        data.day = dayValue;
      }
    }

    _this.update(data);
  }

  private getHoliday(id: number){
    let result = this.oldHolidays.filter(h => h.id === id);
    
    if (result.length >= 0){
      return result[0];
    }

    return null;
  }
}
